import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Message {
  id: string;
  consultation_id: string;
  sender_id: string;
  sender_type: 'seeker' | 'mediator';
  content: string;
  is_read: boolean;
  created_at: string;
}

export const useMessages = (consultationId: string | undefined) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['messages', consultationId],
    queryFn: async () => {
      if (!consultationId) return [];
      
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('consultation_id', consultationId)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data as Message[];
    },
    enabled: !!consultationId && !!user,
    refetchInterval: 10000, // Poll every 10 seconds for new messages
  });
};

export const useSendMessage = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async ({ 
      consultationId, 
      content, 
      senderType 
    }: { 
      consultationId: string; 
      content: string; 
      senderType: 'seeker' | 'mediator';
    }) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('messages')
        .insert({
          consultation_id: consultationId,
          sender_id: user.id,
          sender_type: senderType,
          content: content.trim(),
        })
        .select()
        .single();
      
      if (error) throw error;

      // Send email notification (fire and forget)
      supabase.functions.invoke('send-new-message-email', {
        body: {
          consultationId,
          messageContent: content.trim(),
          senderId: user.id,
          senderType,
        },
      }).catch((emailError) => {
        console.error('Failed to send message notification email:', emailError);
      });

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['messages', variables.consultationId] });
    },
  });
};

export const useMarkMessagesRead = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async ({ 
      consultationId, 
      userType 
    }: { 
      consultationId: string; 
      userType: 'seeker' | 'mediator';
    }) => {
      if (!user?.id) return;
      
      // Mark messages from the other party as read
      const otherType = userType === 'seeker' ? 'mediator' : 'seeker';
      
      const { error } = await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('consultation_id', consultationId)
        .eq('sender_type', otherType)
        .eq('is_read', false);
      
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['messages', variables.consultationId] });
    },
  });
};

export const useUnreadMessageCount = (consultationId: string | undefined, userType: 'seeker' | 'mediator') => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['unreadMessages', consultationId, userType],
    queryFn: async () => {
      if (!consultationId) return 0;
      
      const otherType = userType === 'seeker' ? 'mediator' : 'seeker';
      
      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('consultation_id', consultationId)
        .eq('sender_type', otherType)
        .eq('is_read', false);
      
      if (error) throw error;
      return count || 0;
    },
    enabled: !!consultationId && !!user,
    refetchInterval: 10000,
  });
};
